import React, {useEffect, useRef, useState} from 'react';
import {Col, Row} from 'components/Grid';
import AuthFormsWrapper from "componentsShared/AuthFormsWrapper";
import Alert from "components/Alert";
import Form, {FormItem} from "components/Form";
import {TextField} from "components/TextField";
import useForm from "hooks/useForm";
import {EResetPasswordCasesUI, TResetPasswordCasesUI, TResetPasswordUI} from "data/types/auth.types";
import {useResetPasswordCasesMutation,} from "redux/apiSlices/auth.slice";
import get from "lodash/get";
import Button from "components/Button";
import {Paragraph, Text, Title} from "components/Typography";
import {Colors} from "styles/CssVariables";
import {RoutesNames} from "routes/RoutesNames";
import {Link, useHistory, useLocation} from "react-router-dom";
import {resetPasswordEmailForm} from "./ResetPassword.data";
import Confirmation from "../Confirmation";
import ResetPasswordForm from '../ResetPasswordForm';
import {PageSpinner} from "components/Spinner";


const {email} = resetPasswordEmailForm;

const ResetPassword = () => {
    const history = useHistory();
    const location = useLocation<{ email?: string }>();
    const prefilledEmail = location.state?.email;
    const {form} = useForm<TResetPasswordCasesUI>();
    const [error, setError] = useState<string>('');
    const [resetPassword, {isLoading, data}] = useResetPasswordCasesMutation()
    const alertRef = useRef<HTMLDivElement | null>(null);
    const onSubmit = async (values: TResetPasswordCasesUI) => {
        try {
            await resetPassword(values).unwrap();
            setError('');

        } catch (err) {
            const message = get(err, ['data', 'message']) || 'Something went wrong!';
            setError(message);
        }
    }

    useEffect(() => {
        if (prefilledEmail) {
            form.setFieldsValue({ email: prefilledEmail });
            onSubmit({ email: prefilledEmail });
        }
    }, []);

    useEffect(() => {
        if (error) {
            alertRef.current?.scrollIntoView({block: 'end'})
        }
        if(data?.passwordResendStatus === EResetPasswordCasesUI.RESEND_INVITATION) {
            history.push(RoutesNames.SignIn, {data: EResetPasswordCasesUI.RESEND_INVITATION})
        }
    }, [error, data])
    const emailForForm = prefilledEmail || form.getFieldValue('email');
    const getCorrectForm = (data: TResetPasswordUI) => {
        switch (data.passwordResendStatus) {
            case EResetPasswordCasesUI.RESENT_PASSWORD:
                return <ResetPasswordForm email={emailForForm} isAdmin={data.isAdmin}/>;
            case EResetPasswordCasesUI.RESENT_CODE:
                return <Confirmation email={emailForForm}/>;
            default:
                return null;
        }
    }

    if (data) return getCorrectForm(data);

    // When coming from Login page with email, show loading/error instead of email form
    if (prefilledEmail) {
        if (isLoading) return <PageSpinner centered />;

        if (error) {
            return (
                <Row justify='center'>
                    <AuthFormsWrapper title='Reset your password.'>
                        <Alert type='error' message={error} className='mb-4' />
                        <Paragraph className='mt-2' fontLevel={5}>
                            Go back to{' '}
                            <Text fontLevel={5} color={Colors.DarkBlue} underline>
                                <Link to={RoutesNames.SignIn}>Login page</Link>
                            </Text>
                        </Paragraph>
                    </AuthFormsWrapper>
                </Row>
            );
        }

        return null;
    }

    return (
        <Row justify='center'>
            <AuthFormsWrapper title='Enter your username.'>
                <Title fontLevel={5} className='mb-6'>Enter your credentials to receive a reset link.</Title>
                <Form form={form} onFinish={onSubmit} requiredMark={false} scrollToFirstError>
                    <Row>
                        <Col span={24} ref={alertRef}>
                            <Alert type='error' message={error}/>
                        </Col>
                        <Col span={24}>
                            <FormItem name={email.name} rules={email.rules} label={email.label}>
                                <TextField placeholder={email.placeholder}/>
                            </FormItem>
                        </Col>
                        <Col span={24}>
                            <Button htmlType='submit' loading={isLoading}>Submit</Button>
                            <Paragraph className='mt-2' fontLevel={5}>
                                Go back to{' '}
                                <Text fontLevel={5} color={Colors.DarkBlue} underline>
                                    <Link to={RoutesNames.SignIn}> Login page </Link>
                                </Text>
                            </Paragraph>
                        </Col>
                    </Row>
                </Form>
            </AuthFormsWrapper>
        </Row>
    );
};

export default ResetPassword
